// Mirrors iOS RestaurantNotificationView: notification list with type icons,
// unread dot, tap → mark read + open order, swipe-equivalent delete/read
// buttons, "Mark All as Read", and a bell-slash empty state.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellOff, CheckCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChefHeader from '../shared/ui/ChefHeader';
import { EmptyState, LoadingView } from '../shared/ui';
import {
  listenToNotifications, markNotificationRead,
  markAllNotificationsRead, deleteNotification,
} from '../shared/services/notificationService';
import { fetchOrder } from '../shared/services/orderService';
import NotificationRow from '../components/notifications/NotificationRow';
import NotificationDetailSheet from '../components/notifications/NotificationDetailSheet';

export default function NotificationsPage() {
  const { user, restaurantId } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user?.uid) { setLoading(false); return undefined; }
    const unsub = listenToNotifications(user.uid, (list) => {
      setNotifications(list);
      setLoading(false);
    }, () => setLoading(false), restaurantId);
    return unsub;
  }, [user?.uid, restaurantId]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleTap = async (n) => {
    if (!n.isRead) markNotificationRead(n.id).catch(() => {});
    // Non-order notifications (broadcasts, promos, system alerts) open the
    // detail sheet — mirrors iOS RestaurantNotificationDetailView.
    if (!n.orderId) {
      setSelected(n);
      return;
    }
    // Mirror iOS: verify the order exists before navigating.
    try {
      const order = await fetchOrder(n.orderId);
      if (order) {
        navigate(`/business/orders/${order.id}`);
      } else {
        setError('Order not found. It may have been deleted or you may not have permission to view it.');
      }
    } catch {
      setError('Failed to load order details. Please try again.');
    }
  };

  const handleMarkAll = () => {
    if (user?.uid) markAllNotificationsRead(user.uid, restaurantId).catch(() => {});
  };

  return (
    <div className="pb-6 flex flex-col min-h-full">
      <ChefHeader
        title="Notifications"
        right={unreadCount > 0 && (
          <button
            onClick={handleMarkAll}
            className="flex items-center gap-1 text-xs font-semibold text-[#FF6B35] px-2 py-1.5 rounded-full bg-orange-50 active:bg-orange-100"
          >
            <CheckCheck size={14} />
            Mark all read
          </button>
        )}
      />

      {loading ? (
        <LoadingView label="Loading notifications…" />
      ) : notifications.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={BellOff}
            title="No Notifications"
            subtitle="You're all caught up!"
          />
        </div>
      ) : (
        <>
          <div className="px-4 pt-4 space-y-2">
            {notifications.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onTap={() => handleTap(n)}
                onMarkRead={() => markNotificationRead(n.id).catch(() => {})}
                onDelete={() => deleteNotification(n.id).catch(() => {})}
              />
            ))}
          </div>

          {/* iOS bottom "Mark All as Read" red pill */}
          {unreadCount > 0 && (
            <div className="px-4 mt-4">
              <button
                onClick={handleMarkAll}
                className="w-full py-2 rounded-full bg-[#E5E5EA] text-[#8E8E93] text-sm font-semibold active:scale-[0.98] transition-transform"
              >
                Mark All as Read
              </button>
            </div>
          )}
        </>
      )}

      <NotificationDetailSheet
        notification={selected}
        onClose={() => setSelected(null)}
      />

      {/* Error alert (mirrors iOS alert) */}
      {error && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-8">
          <div className="absolute inset-0 bg-black/40" onClick={() => setError('')} />
          <div className="relative bg-white rounded-2xl p-5 w-full max-w-xs shadow-xl">
            <p className="font-bold text-gray-900">Error</p>
            <p className="text-sm text-gray-500 mt-1">{error}</p>
            <button
              onClick={() => setError('')}
              className="mt-4 w-full py-2 rounded-xl bg-[#FF6B35] text-white text-sm font-bold"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
